// AI Participants with distinct personalities
const AI_PARTICIPANTS = [
  {
    id: 'ai_alex',
    name: 'Alex',
    role: 'Founder of TechFlow',
    avatar: '👨‍💼',
    personality: 'Helpful, experienced, shares startup insights',
    responseStyle: 'When I was scaling my startup...',
    expertise: 'SaaS, fundraising, team building'
  },
  {
    id: 'ai_jordan',
    name: 'Jordan',
    role: 'Growth Marketer',
    avatar: '📈',
    personality: 'Data-driven, enthusiastic about growth hacks',
    responseStyle: 'Here\'s a growth hack that worked for me...',
    expertise: 'Digital marketing, user acquisition, conversion'
  },
  {
    id: 'ai_taylor',
    name: 'Taylor',
    role: 'Product Manager',
    avatar: '🎯',
    personality: 'Analytical, user-focused',
    responseStyle: 'From a product perspective...',
    expertise: 'Product strategy, user research, roadmapping'
  },
  {
    id: 'ai_morgan',
    name: 'Morgan',
    role: 'Tech Lead',
    avatar: '👩‍💻',
    personality: 'Technical, problem-solver',
    responseStyle: 'The technical approach I\'d take...',
    expertise: 'Software architecture, development, scaling'
  },
  {
    id: 'ai_casey',
    name: 'Casey',
    role: 'Investor',
    avatar: '💰',
    personality: 'Direct, focused on metrics',
    responseStyle: 'What\'s your MRR?',
    expertise: 'Fundraising, business metrics, scaling'
  }
] as const;

interface AIParticipant {
  id: string;
  name: string;
  role: string;
  avatar: string;
  personality: string;
  responseStyle: string;
  expertise: string;
}

interface ChatMessage {
  id: string;
  user_id: string;
  pod_id: string;
  message_id: string;
  sender_type: 'user' | 'ai' | 'system';
  sender_name: string;
  content: string;
  timestamp: string;
  created_at: string;
  isSending?: boolean;
  avatar?: string;
  role?: string;
}

class PodChatService {
  private baseUrl = '/functions/v1/pod-chat-manager';

  private scheduleAIResponse(podId: string, userMessage: string) {
    // Randomly select 1-3 AI participants to respond
    const responderCount = Math.floor(Math.random() * 3) + 1;
    const shuffledParticipants = [...AI_PARTICIPANTS]
      .sort(() => 0.5 - Math.random())
      .slice(0, responderCount);

    shuffledParticipants.forEach((ai, index) => {
      // Stagger responses between 1-4 minutes
      const delay = (index * 60 * 1000) + (Math.random() * 3 * 60 * 1000) + 1000;
      
      setTimeout(async () => {
        const response = this.generateAIResponse(ai, userMessage);
        await this.saveAIMessage(ai, podId, response);
      }, delay);
    });
  }

  private generateAIResponse(participant: AIParticipant, message: string): string {
    const responses = [
      `${participant.responseStyle} ${message.includes('?') ? 'Here\'s what I think...' : 'That\'s interesting!'}`,
      `As someone who works in ${participant.expertise.split(', ')[0]}, I'd say...`,
      `${participant.name === 'Casey' ? '💰 ' : ''}${participant.responseStyle} ${participant.name === 'Casey' ? '💰' : ''}`,
      `I remember when I was working on a similar challenge...`
    ];
    
    return responses[Math.floor(Math.random() * responses.length)];
  }

  private async saveAIMessage(ai: AIParticipant, podId: string, content: string): Promise<void> {
    const messageId = `ai_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const message: ChatMessage = {
      id: messageId,
      user_id: ai.id,
      pod_id: podId,
      message_id: messageId,
      sender_type: 'ai',
      sender_name: ai.name,
      content,
      timestamp: new Date().toISOString(),
      created_at: new Date().toISOString(),
      avatar: ai.avatar,
      role: ai.role
    };

    // Save to local storage
    const messages = JSON.parse(localStorage.getItem(`pod_${podId}_messages`) || '[]');
    messages.push(message);
    localStorage.setItem(`pod_${podId}_messages`, JSON.stringify(messages));

    // Notify the UI about the new message
    window.dispatchEvent(new CustomEvent('new-message', {
      detail: { podId }
    }));
  }

  async saveUserMessage(userId: string, podId: string, message: string): Promise<string> {
    try {
      const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Save to local storage
      const messages = JSON.parse(localStorage.getItem(`pod_${podId}_messages`) || '[]');
      const userMessage: ChatMessage = {
        id: messageId,
        user_id: userId,
        pod_id: podId,
        message_id: messageId,
        sender_type: 'user',
        sender_name: 'You',
        content: message,
        timestamp: new Date().toISOString(),
        created_at: new Date().toISOString()
      };
      
      messages.push(userMessage);
      localStorage.setItem(`pod_${podId}_messages`, JSON.stringify(messages));
      
      // Schedule AI responses
      this.scheduleAIResponse(podId, message);
      
      // Notify the UI about the new message
      window.dispatchEvent(new CustomEvent('new-message', {
        detail: { podId }
      }));
      
      return messageId;
    } catch (error) {
      console.error('Error saving user message:', error);
      throw error;
    }
  }

  async loadMessages(userId: string, podId: string): Promise<ChatMessage[]> {
    try {
      // For demo purposes, load from localStorage
      let messages = JSON.parse(localStorage.getItem(`pod_${podId}_messages`) || '[]');
      
      // If no messages, initialize with a welcome message and AI introductions
      if (messages.length === 0) {
        const systemMessage: ChatMessage = {
          id: 'welcome-1',
          user_id: 'system',
          pod_id: podId,
          message_id: 'welcome-1',
          sender_type: 'system',
          sender_name: 'System',
          content: 'Welcome to your pod! This is a space for founders to connect and collaborate. Your AI pod members will join the conversation soon!',
          timestamp: new Date().toISOString(),
          created_at: new Date().toISOString()
        };

        // Add AI introductions
        const aiIntroductions = AI_PARTICIPANTS.map((ai, index) => ({
          id: `ai-intro-${index}`,
          user_id: ai.id,
          pod_id: podId,
          message_id: `ai-intro-${index}`,
          sender_type: 'ai' as const,
          sender_name: ai.name,
          content: `Hi everyone! I'm ${ai.name}, ${ai.role.toLowerCase()}. ${ai.personality} Looking forward to discussing ${ai.expertise || 'startup topics'} with you all!`,
          timestamp: new Date(Date.now() - (AI_PARTICIPANTS.length - index) * 1000 * 60).toISOString(),
          created_at: new Date().toISOString(),
          avatar: ai.avatar,
          role: ai.role
        }));

        messages = [systemMessage, ...aiIntroductions];
        localStorage.setItem(`pod_${podId}_messages`, JSON.stringify(messages));
      }
      
      return messages;
    } catch (error) {
      console.error('Error loading messages:', error);
      return [];
    }
  }

  async cleanupOldMessages(): Promise<void> {
    try {
      // Clean up messages older than 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      // Get all pod message keys from localStorage
      const keys = Object.keys(localStorage).filter(key => key.startsWith('pod_') && key.endsWith('_messages'));
      
      for (const key of keys) {
        try {
          const messages = JSON.parse(localStorage.getItem(key) || '[]');
          const filteredMessages = messages.filter((msg: ChatMessage) => 
            new Date(msg.timestamp) >= thirtyDaysAgo
          );
          
          if (filteredMessages.length < messages.length) {
            localStorage.setItem(key, JSON.stringify(filteredMessages));
          }
        } catch (error) {
          console.error(`Error cleaning up messages for ${key}:`, error);
        }
      }
    } catch (error) {
      console.error('Error cleaning up old messages:', error);
    }
  }

  // Method to detect if a message mentions a specific user
  private detectMentionedUser(message: string, userIds: string[]): string | null {
    // This is a placeholder - in a real app, you'd want to match against actual user mentions
    // For now, we'll just check for @mentions followed by word characters
    const mentionMatch = message.match(/@(\w+)/);
    if (mentionMatch && mentionMatch[1]) {
      const mentionedName = mentionMatch[1].toLowerCase();
      const mentionedUser = userIds.find(id => id.toLowerCase().includes(mentionedName));
      return mentionedUser || null;
    }
    return null;
  }

  // Generate a unique thread ID for direct messages
  private generateThreadId(userId: string, mentionedUserId: string): string {
    const sortedIds = [userId, mentionedUserId].sort();
    return `thread_${sortedIds[0]}_${sortedIds[1]}`;
  }
}

// Create a singleton instance
export const podChatService = new PodChatService();

// Clean up old messages on startup
podChatService.cleanupOldMessages().catch(console.error);
